import type { ReactNode } from 'react';
import { ROLE_LABELS, RoleCode } from '@gb-ticket/shared-types';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useProfileEdit } from '@/platform/features/profile/profile-edit-context';

const ROLE_RANK = [
  RoleCode.IT_ADMIN,
  RoleCode.IT_LEAD,
  RoleCode.IT_AGENT,
  RoleCode.END_USER,
] as const;

export function initialsOf(firstName: string, lastName: string, displayName: string): string {
  const pair = `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();
  if (pair) {
    return pair;
  }
  return displayName.trim().slice(0, 2).toUpperCase() || '?';
}

export function roleLabelOf(roles: readonly RoleCode[]): string | null {
  const ranked = ROLE_RANK.find((role) => roles.includes(role));
  return ranked ? ROLE_LABELS[ranked] : null;
}

export function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate">{value}</p>
    </div>
  );
}

const MONTHS = [
  { value: 1, label: 'Januar' },
  { value: 2, label: 'Februar' },
  { value: 3, label: 'März' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Dezember' },
];

/** Geburtstag und Jubiläum — Speichern über die Profilseite. */
export function BirthdayFields() {
  const {
    birthdayMonth,
    setBirthdayMonth,
    birthdayDay,
    setBirthdayDay,
    showBirthday,
    setShowBirthday,
    hiredOn,
    setHiredOn,
  } = useProfileEdit();

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="birthday-month">Monat</Label>
          <Select
            value={birthdayMonth || 'none'}
            onValueChange={(value) => setBirthdayMonth(value === 'none' ? '' : value)}
          >
            <SelectTrigger id="birthday-month" className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein Datum</SelectItem>
              {MONTHS.map((item) => (
                <SelectItem key={item.value} value={String(item.value)}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="birthday-day">Tag</Label>
          <Input
            id="birthday-day"
            className="mt-2"
            type="number"
            min={1}
            max={31}
            value={birthdayDay}
            onChange={(event) => setBirthdayDay(event.target.value)}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2">
        <Label htmlFor="show-birthday" className="text-sm font-normal">
          Geburtstag im Intranet anzeigen
        </Label>
        <Switch
          id="show-birthday"
          checked={showBirthday}
          disabled={!birthdayMonth || !birthdayDay}
          onCheckedChange={setShowBirthday}
        />
      </div>
      <div>
        <Label htmlFor="hired-on">Eintrittsdatum</Label>
        <Input
          id="hired-on"
          className="mt-2"
          type="date"
          value={hiredOn}
          onChange={(event) => setHiredOn(event.target.value)}
        />
      </div>
    </div>
  );
}

export function ProfileSettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 px-4 py-5 sm:px-6">
      <div className="space-y-1">
        <h2 className="text-base font-semibold tracking-tight text-primary">{title}</h2>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function ProfilePageShell({ children }: { children: ReactNode }) {
  return <section className="mx-auto max-w-3xl space-y-6">{children}</section>;
}
